import { ReactNode } from 'react';
import { Grid, Box, GridSize } from '@mui/material';
import {
    SttFormText,
    SttFormTextarea,
    SttFormDate,
    SttFormSelect,
    SttButtonSave,
    SttButtonDelete,
    SttButton,
    SttButtonAccept,
    SttFormCheck,
    SttButtonRecover,
    SttSelectCity,
} from './all';
import { usePermission } from '../lib/usePermission';
import { PermissionRequirement } from '../lib/permissions';

type FormElement = {
    name?: string;
    caption?: string;
    value?: string;
    type?: string;
    md?: boolean | GridSize;
    sm?: boolean | GridSize;
    xs?: boolean | GridSize;
    disabled?: boolean;
    options?: { value: string; label: string }[];
    rows?: number;
    show?: boolean;
    password?: boolean;
    component?: ReactNode;
};

type FormButton = {
    caption?: string;
    onClick: () => void;
    show?: boolean;
    type: 'save' | 'delete' | 'accept' | 'recover';
    /** Permiso v8 que hace falta para ver el botón (p. ej. `<módulo>.modify`). */
    permission?: PermissionRequirement;
};

type SttFormFormProps = {
    elements?: FormElement[];
    buttons?: FormButton[];
    onChangeForm?: ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => void;
};

export function SttForm({ elements, buttons, onChangeForm }: SttFormFormProps) {
    const { can } = usePermission();

    const renderElement = (ele: FormElement, idx: number) => {
        let content;
        const {
            name,
            caption,
            value,
            type = 'input',
            md,
            sm,
            xs,
            disabled,
            options,
            show = true,
            password,
            component,
        } = ele;

        switch (type ? type.toLowerCase() : 'input') {
            case 'date':
                content = (
                    <SttFormDate
                        name={name}
                        caption={caption}
                        defVal={value}
                        disabled={disabled}
                        onChange={onChangeForm}
                    />
                );
                break;

            case 'textarea':
                content = (
                    <SttFormTextarea
                        name={name}
                        rows={ele.rows}
                        caption={caption}
                        defVal={value || ''}
                        disabled={disabled}
                        onChange={onChangeForm}
                    />
                );
                break;

            case 'select':
                content = (
                    <SttFormSelect
                        name={name}
                        caption={caption}
                        defVal={value || ''}
                        disabled={disabled}
                        onChange={onChangeForm}
                        options={options}
                    />
                );
                break;

            case 'check':
                content = (
                    <SttFormCheck
                        name={name}
                        caption={caption}
                        disabled={disabled}
                        checked={Boolean(value) || false}
                        onChange={(event: any) =>
                            onChangeForm?.({
                                value: event.value.toString(),
                                name: event.name,
                            })
                        }
                    />
                );
                break;

            case 'city':
                content = (
                    <SttSelectCity
                        name={name || ''}
                        caption={caption || ''}
                        disabled={disabled}
                        defVal={value || ''}
                        onChange={(event) =>
                            onChangeForm?.({
                                name: event.name,
                                value: event.value,
                            })
                        }
                        onSelect={(event) =>
                            onChangeForm?.({
                                name: event.name,
                                value: event.value,
                            })
                        }
                    />
                );
                break;

            case 'component':
                if (component) {
                    content = component;
                }
                break;

            default:
                content = (
                    <SttFormText
                        name={name}
                        caption={caption}
                        defVal={value || ''}
                        disabled={disabled}
                        password={password}
                        onChange={onChangeForm}
                    />
                );
        }

        if (show) {
            // Filter out boolean true values which aren't supported in MUI v5 Grid size prop
            const sizeProps = {
                md: typeof md === 'boolean' ? undefined : md,
                sm: typeof sm === 'boolean' ? undefined : sm,
                xs: typeof xs === 'boolean' ? undefined : xs,
            };
            return (
                <Grid key={idx} size={sizeProps}>
                    {content}
                </Grid>
            );
        }
        return null;
    };

    const renderButton = (button: FormButton, idx: number) => {
        let content;
        const { caption, onClick, show = true, type, permission } = button;

        if (permission && !can(permission)) return null;

        switch (type) {
            case 'save':
                content = <SttButtonSave fullWidth onClick={onClick} />;
                break;

            case 'delete':
                content = <SttButtonDelete fullWidth onClick={onClick} />;
                break;

            case 'accept':
                content = <SttButtonAccept fullWidth onClick={onClick} />;
                break;

            case 'recover':
                content = <SttButtonRecover fullWidth onClick={onClick} />;
                break;

            default:
                content = (
                    <SttButton fullWidth caption={caption} onClick={onClick} />
                );
        }

        if (show) {
            return (
                <Grid key={idx} size={{ xs: 12, sm: 12, md: 3 }}>
                    {content}
                </Grid>
            );
        }
    };

    return (
        <>
            <Grid container spacing={3}>
                {elements?.map((e, idx) => renderElement(e, idx))}
            </Grid>

            {buttons && (
                <Box mt={4}>
                    <Grid container spacing={3}>
                        {buttons.map(renderButton)}
                    </Grid>
                </Box>
            )}
        </>
    );
}
